// components/admin/ImageManagement.jsx
import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Card, CardContent, Grid, Button,
  LinearProgress, Alert, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Tabs, Tab,
  FormControlLabel, Checkbox, Collapse, Tooltip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  TextField, Snackbar,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import axiosInstance from '../../axiosInstance';
import AppBarHeader from '../AppBarHeader';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

const COLLECTION_TYPES = [
  { key: 'consoles', label: 'Consoles', fetchPath: (uid) => `/consoles/user/${uid}` },
  { key: 'lego', label: 'LEGO Sets', fetchPath: (uid) => `/lego/user/${uid}` },
  { key: 'music', label: 'Music', fetchPath: (uid) => `/music/user/${uid}` },
  { key: 'games', label: 'Games', fetchPath: (uid) => `/games/user/${uid}` },
  { key: 'movies', label: 'Movies', fetchPath: (uid) => `/movies/user/${uid}` },
  { key: 'books', label: 'Books', fetchPath: (uid) => `/books/user/${uid}` },
  { key: 'board-games', label: 'Board Games', fetchPath: (uid) => `/board-games/user/${uid}` },
];

const ImageManagement = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const user = token ? jwtDecode(token) : null;
  const userId = user ? user.id : null;
  const isAdmin = user ? user.isAdmin : false;

  const [mainTab, setMainTab] = useState(0);
  const [stats, setStats] = useState({});
  const [processing, setProcessing] = useState(false);
  const [processingType, setProcessingType] = useState('');
  const [result, setResult] = useState(null);
  const [adminAll, setAdminAll] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [expandedRun, setExpandedRun] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, mode: '', itemType: '' });
  const [failedItems, setFailedItems] = useState([]);
  const [failedItemsOpen, setFailedItemsOpen] = useState(false);

  // Gallery state
  const [gallery, setGallery] = useState({ items: [], total: 0, page: 1 });
  const [galleryType, setGalleryType] = useState('all');
  const [galleryStatus, setGalleryStatus] = useState('all');
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryError, setGalleryError] = useState(null);
  const [beforeAfterModal, setBeforeAfterModal] = useState(null);
  const [replaceSourceField, setReplaceSourceField] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [itemLoading, setItemLoading] = useState({}); // itemId -> true while action in progress
  const [gallerySnackbar, setGallerySnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  useEffect(() => {
    loadStats();
    loadHistory();
  }, []);

  useEffect(() => {
    if (mainTab === 1) loadGallery(1);
  }, [mainTab, galleryType, galleryStatus]);

  const loadStats = async () => {
    try {
      if (!userId) return;
      const results = await Promise.allSettled(
        COLLECTION_TYPES.map(t => axiosInstance.get(t.fetchPath(userId)))
      );
      const newStats = {};
      COLLECTION_TYPES.forEach((t, i) => {
        const items = results[i].status === 'fulfilled' ? results[i].value.data : [];
        const total = items.length;
        const processed = items.filter(item => item.processedImageUrl).length;
        const failed = items.filter(item => item.processingError).length;
        newStats[t.key] = { total, processed, unprocessed: total - processed, failed };
      });
      setStats(newStats);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadHistory = async () => {
    try {
      const res = await axiosInstance.get('/image-processor/history?limit=20');
      setHistory(res.data);
    } catch (error) {
      console.error('Failed to load history:', error);
    }
  };

  const loadGallery = async (page = 1) => {
    setGalleryLoading(true);
    setGalleryError(null);
    try {
      const res = await axiosInstance.get(
        `/image-processor/gallery?type=${galleryType}&status=${galleryStatus}&page=${page}&limit=50`
      );
      setGallery({ items: res.data.items, total: res.data.total, page });
    } catch (err) {
      setGalleryError('Failed to load gallery');
    } finally {
      setGalleryLoading(false);
    }
  };

  const runBatch = async (itemType, mode) => {
    setProcessing(true);
    setProcessingType(itemType);
    setResult(null);
    setFailedItems([]);
    try {
      const batchResult = await axiosInstance.post('/image-processor/process-batch', {
        itemType, mode, adminAll,
      });
      const { successCount, failedCount, totalCount, results } = batchResult.data;
      const failed = (results || []).filter(r => !r.success);
      setFailedItems(failed);
      setFailedItemsOpen(failed.length > 0);
      setResult({
        success: true,
        message: totalCount === 0
          ? 'Nothing to process.'
          : `Processed ${successCount} of ${totalCount}. Failed: ${failedCount}.`,
      });
      await loadStats();
      await loadHistory();
    } catch (error) {
      setResult({ success: false, message: 'Batch failed: ' + (error.response?.data?.error || error.message) });
    } finally {
      setProcessing(false);
      setProcessingType('');
    }
  };

  const handleProcessUnprocessed = (itemType) => runBatch(itemType, 'unprocessed');
  const handleReprocessAll = (itemType) => setConfirmDialog({ open: true, mode: 'all', itemType });
  const handleReprocessFailed = (itemType) => setConfirmDialog({ open: true, mode: 'failed', itemType });
  const handleConfirmDialogClose = (confirmed) => {
    const { mode, itemType } = confirmDialog;
    setConfirmDialog({ open: false, mode: '', itemType: '' });
    if (confirmed) runBatch(itemType, mode);
  };

  const fixStoredUrls = async () => {
    setProcessing(true);
    setResult(null);
    try {
      const response = await axiosInstance.get('/image-processor/fix-urls');
      const fixed = response.data.results?.reduce((sum, r) => sum + r.modified, 0) || 0;
      setResult({ success: true, message: `Fixed ${fixed} broken URL record(s).` });
    } catch (error) {
      setResult({ success: false, message: 'Fix failed: ' + (error.response?.data?.error || error.message) });
    } finally {
      setProcessing(false);
    }
  };

  const fixConsoleImages = async () => {
    setProcessing(true);
    setResult(null);
    try {
      const response = await axiosInstance.post('/fix-console-images');
      setResult({ success: true, message: `Updated ${response.data.updated} console(s) with Wikipedia hardware images.` });
      await loadStats();
    } catch (error) {
      setResult({ success: false, message: 'Console fix failed: ' + (error.response?.data?.error || error.message) });
    } finally {
      setProcessing(false);
    }
  };

  const setItemBusy = (id, busy) => setItemLoading(prev => ({ ...prev, [id]: busy }));
  const showGalleryMsg = (message, severity = 'success') => setGallerySnackbar({ open: true, message, severity });

  // Gallery handlers
  const handleGalleryReprocess = async (item) => {
    setItemBusy(item._id, true);
    try {
      await axiosInstance.post('/image-processor/process-item', {
        itemType: item.collectionType, itemId: item._id,
      });
      showGalleryMsg(`Reprocessed: ${item.name}`);
      await loadGallery(gallery.page);
    } catch (err) {
      showGalleryMsg('Reprocess failed: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setItemBusy(item._id, false);
    }
  };

  const handleGallerySaveSource = async (item) => {
    const newUrl = replaceSourceField[item._id];
    if (!newUrl) return;
    setItemBusy(item._id, true);
    try {
      await axiosInstance.patch(`/image-processor/source/${item.collectionType}/${item._id}`, { imageUrl: newUrl });
      setReplaceSourceField(prev => ({ ...prev, [item._id]: '' }));
      showGalleryMsg('Source URL updated.');
      await loadGallery(gallery.page);
    } catch (err) {
      showGalleryMsg('Replace source failed: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setItemBusy(item._id, false);
    }
  };

  const handleGalleryClearProcessed = async (item) => {
    setItemBusy(item._id, true);
    try {
      await axiosInstance.delete(`/image-processor/processed-image/${item.collectionType}/${item._id}`);
      showGalleryMsg('Processed image cleared.');
      await loadGallery(gallery.page);
    } catch (err) {
      showGalleryMsg('Clear failed: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setItemBusy(item._id, false);
    }
  };

  const handleGalleryDelete = async () => {
    if (!deleteConfirm) return;
    const { itemType, itemId } = deleteConfirm;
    setDeleteConfirm(null);
    setItemBusy(itemId, true);
    try {
      await axiosInstance.delete(`/image-processor/image/${itemType}/${itemId}`);
      showGalleryMsg('Images cleared from item.');
      await loadGallery(gallery.page);
    } catch (err) {
      showGalleryMsg('Delete failed: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setItemBusy(itemId, false);
    }
  };

  const StatCard = ({ title, typeKey }) => {
    const s = stats[typeKey] || { total: 0, processed: 0, unprocessed: 0, failed: 0 };
    const isProcessingThis = processing && processingType === typeKey;
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>{title}</Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">Total: {s.total}</Typography>
            <Typography variant="body2" color="success.main">Processed: {s.processed}</Typography>
            <Typography variant="body2" color="warning.main">Unprocessed: {s.unprocessed}</Typography>
            <Typography variant="body2" color="error.main">Failed: {s.failed}</Typography>
          </Box>
          {isProcessingThis && <LinearProgress sx={{ mb: 1 }} />}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button variant="contained" color="primary" startIcon={<AutoFixHighIcon />}
              onClick={() => handleProcessUnprocessed(typeKey)}
              disabled={processing || s.unprocessed === 0} fullWidth size="small">
              Process Unprocessed ({s.unprocessed})
            </Button>
            <Button variant="outlined" color="secondary"
              onClick={() => handleReprocessAll(typeKey)}
              disabled={processing || s.total === 0} fullWidth size="small">
              Reprocess All ({s.total})
            </Button>
            <Button variant="outlined" color="error"
              onClick={() => handleReprocessFailed(typeKey)}
              disabled={processing || s.failed === 0} fullWidth size="small">
              Reprocess Failed ({s.failed})
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <>
      <AppBarHeader isAdmin={isAdmin} userId={userId} onLogout={handleLogout} />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom>Image Processing Management</Typography>
        <Button variant="outlined" onClick={() => navigate('/admin')} sx={{ mb: 2 }}>
          ← Back to Admin Dashboard
        </Button>

        <Tabs value={mainTab} onChange={(_, v) => setMainTab(v)} sx={{ mb: 3 }}>
          <Tab label="Processing" />
          <Tab label="Gallery" />
        </Tabs>

        {mainTab === 0 && (
          <>
            {result && (
              <Alert severity={result.success ? 'success' : 'error'} sx={{ mb: 2 }} onClose={() => setResult(null)}>
                {result.message}
              </Alert>
            )}

            {failedItems.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Button size="small" endIcon={failedItemsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  onClick={() => setFailedItemsOpen(v => !v)}>
                  Failed items ({failedItems.length})
                </Button>
                <Collapse in={failedItemsOpen}>
                  <TableContainer component={Paper} sx={{ mt: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Item ID</TableCell>
                          <TableCell>Error</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {failedItems.map((f, i) => (
                          <TableRow key={i}>
                            <TableCell>{String(f.itemId)}</TableCell>
                            <TableCell>{f.error}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Collapse>
              </Box>
            )}

            <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button variant="outlined" color="warning" onClick={fixStoredUrls} disabled={processing} size="small">
                Fix Broken Stored URLs
              </Button>
              <Button variant="contained" color="primary" onClick={fixConsoleImages} disabled={processing} size="small">
                Fix Console Images (Logo → Hardware)
              </Button>
              <FormControlLabel
                control={<Checkbox checked={adminAll} onChange={e => setAdminAll(e.target.checked)} />}
                label="Process all users (system-wide)"
              />
            </Box>

            <Grid container spacing={2} sx={{ mb: 4 }}>
              {COLLECTION_TYPES.map(t => (
                <Grid item xs={12} sm={6} md={4} key={t.key}>
                  <StatCard title={t.label} typeKey={t.key} />
                </Grid>
              ))}
            </Grid>

            <Box>
              <Button endIcon={historyOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={() => setHistoryOpen(v => !v)} variant="text">
                Processing History ({history.length})
              </Button>
              <Collapse in={historyOpen}>
                <TableContainer component={Paper} sx={{ mt: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Processed</TableCell>
                        <TableCell>Failed</TableCell>
                        <TableCell>Duration</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {history.map(run => (
                        <React.Fragment key={run._id}>
                          <TableRow>
                            <TableCell>{new Date(run.runAt).toLocaleString()}</TableCell>
                            <TableCell>{run.collectionType}</TableCell>
                            <TableCell>{run.processed}</TableCell>
                            <TableCell>{run.failed}</TableCell>
                            <TableCell>{run.durationMs}ms</TableCell>
                            <TableCell>
                              {run.failedItems?.length > 0 && (
                                <IconButton size="small" onClick={() => setExpandedRun(expandedRun === run._id ? null : run._id)}>
                                  {expandedRun === run._id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                          {expandedRun === run._id && run.failedItems?.map((fi, i) => (
                            <TableRow key={i} sx={{ backgroundColor: 'action.hover' }}>
                              <TableCell colSpan={2}>{fi.name || String(fi.itemId)}</TableCell>
                              <TableCell colSpan={4}>{fi.error}</TableCell>
                            </TableRow>
                          ))}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Collapse>
            </Box>
          </>
        )}

        {mainTab === 1 && (
          <>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3, alignItems: 'center' }}>
              <TextField select label="Collection type" value={galleryType}
                onChange={e => setGalleryType(e.target.value)} size="small" sx={{ minWidth: 150 }}
                SelectProps={{ native: true }}>
                <option value="all">All types</option>
                {COLLECTION_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </TextField>
              <TextField select label="Status" value={galleryStatus}
                onChange={e => setGalleryStatus(e.target.value)} size="small" sx={{ minWidth: 140 }}
                SelectProps={{ native: true }}>
                <option value="all">All</option>
                <option value="processed">Processed</option>
                <option value="unprocessed">Unprocessed</option>
                <option value="failed">Failed</option>
              </TextField>
              <Button variant="outlined" size="small" onClick={() => loadGallery(1)}>Refresh</Button>
              <Typography variant="body2" color="text.secondary">
                {gallery.total} item{gallery.total !== 1 ? 's' : ''}
              </Typography>
            </Box>

            {galleryError && <Alert severity="error" sx={{ mb: 2 }}>{galleryError}</Alert>}
            {galleryLoading && <LinearProgress sx={{ mb: 2 }} />}

            <Grid container spacing={2}>
              {gallery.items.map(item => {
                const thumb = item.processedImageUrl || item.imageUrl;
                const statusColor = item.processingStatus === 'processed' ? 'success'
                  : item.processingStatus === 'failed' ? 'error' : 'warning';
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={`${item.collectionType}-${item._id}`}>
                    <Card>
                      <Box sx={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}>
                        {thumb ? (
                          <img src={thumb} alt={item.name} style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} />
                        ) : (
                          <Typography variant="body2" color="text.secondary">No image</Typography>
                        )}
                      </Box>
                      <CardContent sx={{ py: 1 }}>
                        <Typography variant="body2" noWrap title={item.name}>{item.name}</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                          <Chip label={item.collectionType} size="small" variant="outlined" />
                          <Tooltip title={item.processingError || ''}>
                            <Chip label={item.processingStatus} size="small" color={statusColor} />
                          </Tooltip>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                          <Button size="small" variant="outlined"
                            onClick={() => handleGalleryReprocess(item)}
                            disabled={!!itemLoading[item._id]}>
                            {itemLoading[item._id] ? 'Processing…' : 'Reprocess'}
                          </Button>
                          {item.processedImageUrl && item.imageUrl && (
                            <Button size="small" variant="outlined"
                              disabled={!!itemLoading[item._id]}
                              onClick={() => setBeforeAfterModal({ imageUrl: item.imageUrl, processedImageUrl: item.processedImageUrl, name: item.name })}>
                              Before/After
                            </Button>
                          )}
                          {item.processedImageUrl && (
                            <Button size="small" variant="outlined" color="warning"
                              disabled={!!itemLoading[item._id]}
                              onClick={() => handleGalleryClearProcessed(item)}>
                              Clear Processed
                            </Button>
                          )}
                          <Button size="small" variant="outlined" color="error"
                            disabled={!!itemLoading[item._id]}
                            onClick={() => setDeleteConfirm({ itemType: item.collectionType, itemId: item._id })}>
                            Delete
                          </Button>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
                          <TextField size="small" placeholder="New source URL"
                            value={replaceSourceField[item._id] || ''}
                            onChange={e => setReplaceSourceField(prev => ({ ...prev, [item._id]: e.target.value }))}
                            sx={{ flex: 1 }}
                            inputProps={{ style: { fontSize: '0.75rem', padding: '4px 8px' } }}
                          />
                          <Button size="small" onClick={() => handleGallerySaveSource(item)}
                            disabled={!replaceSourceField[item._id] || !!itemLoading[item._id]}>
                            Save
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {gallery.total > 50 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
                <Button disabled={gallery.page <= 1} onClick={() => loadGallery(gallery.page - 1)}>Previous</Button>
                <Typography sx={{ alignSelf: 'center' }}>
                  Page {gallery.page} of {Math.ceil(gallery.total / 50)}
                </Typography>
                <Button disabled={gallery.page >= Math.ceil(gallery.total / 50)} onClick={() => loadGallery(gallery.page + 1)}>Next</Button>
              </Box>
            )}
          </>
        )}
      </Container>

      {/* Confirm: Reprocess All / Failed */}
      <Dialog open={confirmDialog.open} onClose={() => handleConfirmDialogClose(false)}>
        <DialogTitle>
          {confirmDialog.mode === 'all' ? 'Reprocess All' : 'Reprocess Failed'} — {confirmDialog.itemType}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.mode === 'all'
              ? 'This will reprocess ALL items, overwriting existing processed images.'
              : 'This will reprocess items that previously failed. Continue?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleConfirmDialogClose(false)}>Cancel</Button>
          <Button onClick={() => handleConfirmDialogClose(true)} color="warning" variant="contained">Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* Before/After modal */}
      <Dialog open={!!beforeAfterModal} onClose={() => setBeforeAfterModal(null)} maxWidth="md" fullWidth>
        <DialogTitle>Before / After — {beforeAfterModal?.name}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="caption" display="block" gutterBottom>Original</Typography>
              <img src={beforeAfterModal?.imageUrl} alt="Original" style={{ maxWidth: '100%' }} />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" display="block" gutterBottom>Processed</Typography>
              <img src={beforeAfterModal?.processedImageUrl} alt="Processed" style={{ maxWidth: '100%' }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBeforeAfterModal(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete image confirmation */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Image</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently clear the image URLs for this item. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button onClick={handleGalleryDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Gallery action feedback */}
      <Snackbar
        open={gallerySnackbar.open}
        autoHideDuration={4000}
        onClose={() => setGallerySnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={gallerySnackbar.severity} onClose={() => setGallerySnackbar(s => ({ ...s, open: false }))}>
          {gallerySnackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ImageManagement;
